export const USER_ANALYTICS_DISABLED_KEY = 'studyapp_user_analytics_disabled';

const SUBSCRIPTIONS_KEY = 'studyapp_subscriptions';

const LOCAL_STORAGE_SESSION_KEYS = [
  'studyapp.com',
  'studyapp.com_firstname',
  'studyapp.com_username',
  'studyapp.com_userID',
  'studyapp_role',
  'studyapp_admin',
  'studyapp_parentId',
  // Legacy flat subscription keys — kept here so clearAuthSessionStorage removes them on logout.
  'studyapp_subscription_status',
  'studyapp_subscription_endDate',
  'studyapp_subscription_startDate',
  'studyapp_subscription_planId',
  'studyapp_subscription_nextBillingAt',
  'studyapp_subscription_cancelAtPeriodEnd',
  'studyapp_subscription_paymentCommitted',
  'studyapp_subscription_committed',
  // New subscriptions array key
  SUBSCRIPTIONS_KEY,
  'activeCourseId',
  'selectedStudentID',
  'selectedStudent_name',
  'sessionExpiredNotified',
  'intendedLoginPath',
  'returnTo',
  'user_isVerified',
  USER_ANALYTICS_DISABLED_KEY
];

const SESSION_STORAGE_SESSION_KEYS = [
  'sessionOnly'
];

const REAL_EXAM_STORAGE_PREFIX = 'real-exam:';

export function setStoredUserAnalyticsDisabled(isDisabled) {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    if (isDisabled) {
      window.localStorage.setItem(USER_ANALYTICS_DISABLED_KEY, '1');
    } else {
      window.localStorage.removeItem(USER_ANALYTICS_DISABLED_KEY);
    }
  } catch (error) {
    // Ignore storage failures so auth flows are not blocked by localStorage issues.
  }
}

export function isStoredUserAnalyticsDisabled() {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return window.localStorage.getItem(USER_ANALYTICS_DISABLED_KEY) === '1';
  } catch (error) {
    return false;
  }
}

export function getStoredSubscriptions() {
  if (typeof window === 'undefined') {
    return [];
  }
  try {
    const raw = window.localStorage.getItem(SUBSCRIPTIONS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    }
    // Backward compat: reconstruct a legacy entry from old flat keys so users logged in
    // before this update don't lose their subscription state on reload.
    const status = window.localStorage.getItem('studyapp_subscription_status');
    if (status) {
      return [{
        // No courseId — legacy global subscription; isSubscriptionEntitled handles this.
        status,
        endDate: window.localStorage.getItem('studyapp_subscription_endDate') || null,
        startDate: window.localStorage.getItem('studyapp_subscription_startDate') || null,
        planKey: window.localStorage.getItem('studyapp_subscription_planId') || null,
        nextBillingAt: window.localStorage.getItem('studyapp_subscription_nextBillingAt') || null,
        cancelAtPeriodEnd: window.localStorage.getItem('studyapp_subscription_cancelAtPeriodEnd') === 'true',
        paymentCommitted: window.localStorage.getItem('studyapp_subscription_paymentCommitted') === 'true',
      }];
    }
    return [];
  } catch (error) {
    return [];
  }
}

export function setStoredSubscriptions(subscriptions) {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    if (Array.isArray(subscriptions)) {
      window.localStorage.setItem(SUBSCRIPTIONS_KEY, JSON.stringify(subscriptions));
    }
  } catch (error) {
    // Ignore storage failures so auth flows are not blocked.
  }
}

export function getStoredSubscriptionForCourse(courseId) {
  if (!courseId) return null;
  const subscriptions = getStoredSubscriptions();
  return subscriptions.find(
    s => s.courseId && s.courseId.toString() === String(courseId)
  ) || null;
}

export function syncCurrentUserProfileToStorage(user = {}) {
  if (typeof window === 'undefined' || !user || typeof user !== 'object') {
    return;
  }

  if (typeof user.analyticsDisabled === 'boolean') {
    setStoredUserAnalyticsDisabled(user.analyticsDisabled);
  }
  if (typeof user.isVerified !== 'undefined') {
    window.localStorage.setItem('user_isVerified', String(!!user.isVerified));
  }
  if (typeof user.role === 'string' && user.role) {
    window.localStorage.setItem('studyapp_role', user.role);
  }
  if (typeof user.admin !== 'undefined') {
    window.localStorage.setItem('studyapp_admin', String(!!user.admin));
  }
  // Sync subscriptions array so cached state stays aligned with server truth.
  if (Array.isArray(user.subscriptions)) {
    setStoredSubscriptions(user.subscriptions);
  }
}

export function clearAuthSessionStorage() {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    LOCAL_STORAGE_SESSION_KEYS.forEach((key) => window.localStorage.removeItem(key));

    for (let index = window.localStorage.length - 1; index >= 0; index -= 1) {
      const key = window.localStorage.key(index);
      if (typeof key === 'string' && key.startsWith(REAL_EXAM_STORAGE_PREFIX)) {
        window.localStorage.removeItem(key);
      }
    }

    SESSION_STORAGE_SESSION_KEYS.forEach((key) => window.sessionStorage.removeItem(key));
  } catch (error) {
    // Ignore storage cleanup failures so logout can still proceed in memory.
  }
}
